import type { FC, ReactNode } from 'react'

import { Tag } from '@/components/generals/tag'
import { BackLink } from '@/components/generals/back-link'

type LegalSection = {
    id: string
    heading: string
    paragraphs: string[]
    bullets?: string[]
}

type LegalPageProps = {
    tag: string
    title: string
    updated: string
    intro: string
    sections: LegalSection[]
}

export const LegalPage: FC<LegalPageProps> = ({
    tag,
    title,
    updated,
    intro,
    sections
}): ReactNode => {
    return (
        <article className='px-0 py-12 tab:px-[60px] tab:py-20'>
            <div className='fw-container flex max-w-[860px] flex-col gap-10'>
                <div data-load='up' data-load-delay='0' data-load-duration='1.1'>
                    <BackLink href='/'>
                        back to home
                    </BackLink>
                </div>

                <header data-load='up' data-load-delay='0.08' data-load-duration='1.1' className='flex flex-col gap-4 border-b border-line pb-10'>
                    <div>
                        <Tag>
                            {tag}
                        </Tag>
                    </div>

                    <h1 className='font-sans text-[43px] font-normal leading-[1em] tracking-[-0.045em] text-ink tab:text-[54px]'>
                        {title}
                    </h1>

                    <p className='font-body text-[17px] leading-[1.45em] text-ink-soft'>
                        {intro}
                    </p>

                    <p className='font-body text-[14px] leading-[1.4em] text-ink-mute'>
                        Last updated {updated}
                    </p>
                </header>

                <div className='flex flex-col gap-10'>
                    {sections.map((section, index) => (
                        <section
                            key={section.id}
                            id={section.id}
                            data-reveal='up'
                            data-reveal-delay={0.04 * index}
                            className='flex scroll-mt-[110px] flex-col gap-4'
                        >
                            <h2 className='font-sans text-[22px] font-medium leading-[1.2em] text-ink tab:text-[26px]'>
                                {section.heading}
                            </h2>

                            {section.paragraphs.map((paragraph) => (
                                <p key={paragraph} className='font-body text-[16px] leading-[1.5em] text-ink-soft'>
                                    {paragraph}
                                </p>
                            ))}

                            {section.bullets ? (
                                <ul className='flex flex-col gap-2 pl-5'>
                                    {section.bullets.map((bullet) => (
                                        <li
                                            key={bullet}
                                            className='list-disc font-body text-[16px] leading-[1.5em] text-ink-soft marker:text-ink-mute'
                                        >
                                            {bullet}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </section>
                    ))}
                </div>
            </div>
        </article>
    )
}
